import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import {getColors, getSizes} from '../../products/cors/_requests'
import {IColorsModel, ISizesModel} from '../../products/cors/_models'
import {requestVariantFromBarcode} from '../cors/_requests'
import ImagePicker from './ImagePicker'
import './variant-multi-offer.css'

/**
 * "Add a variant on a used barcode" — queues one or more NEW size/color cards
 * and submits them together; each files a catalog request (POST
 * /offers/request-variant) and the offer is created on approval.
 *
 * Trigger: full-width blue "+ Add new variant Size / Color" button (Figma 10).
 * Each card: SIZE | COLOR dropdowns, SALE PRICE | SUPPLIER QUANTITY, SKU,
 * Cancel + Submit Request buttons.
 */

type RowStatus = 'idle' | 'submitting' | 'done' | 'error'

type VRow = {
  id: number
  sizeId: number
  colorId: number
  price: string
  qty: string
  sku: string
  /** New-COLOR variants only: photos for the new colour (sent with the request). */
  images: File[]
  status: RowStatus
  error?: string
}

const toNum = (v: string): number => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : NaN
}

const VARIANT_ERR_CODES = [
  'variation_required',
  'variant_already_exists',
  'variant_request_pending',
  'variant_master_mismatch',
  'barcode_family_unmanaged',
  'barcode_family_ambiguous',
  'barcode_not_in_catalog',
  'price_invalid',
  'quantity_invalid',
]

const AddVariantsPanel: FC<{
  barcode: string
  seedImages?: File[]
  /** The product's existing variant colour ids. When provided, a chosen colour
   *  NOT in this list is "new" and must carry its own photo(s); an existing
   *  colour (or a size-only change) reuses the master's images, so the photo is
   *  optional. When omitted (single-product card), photos stay optional. */
  existingColorIds?: number[]
}> = ({barcode, seedImages, existingColorIds}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()

  const [colorOpts, setColorOpts] = useState<IColorsModel[]>([])
  const [sizeOpts, setSizeOpts] = useState<ISizesModel[]>([])
  const [optsLoaded, setOptsLoaded] = useState(false)
  const [rows, setRows] = useState<VRow[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [allDone, setAllDone] = useState(false)

  const ensureOptions = async () => {
    if (optsLoaded) return
    try {
      const [c, s] = await Promise.all([
        getColors(currentUser?.token, language || 'en'),
        getSizes(currentUser?.token, language || 'en'),
      ])
      setColorOpts(Array.isArray(c.data) ? c.data : c.data?.data ?? [])
      setSizeOpts(Array.isArray(s.data) ? s.data : s.data?.data ?? [])
    } catch {
      /* selects stay empty; submit is still validated server-side */
    }
    setOptsLoaded(true)
  }

  const addRow = () => {
    void ensureOptions()
    setAllDone(false)
    setRows((prev) => {
      const nextId = (prev.length ? Math.max(...prev.map((r) => r.id)) : 0) + 1
      // Pre-fill with the photos the supplier uploaded in the bulk image-zip
      // for this barcode (shown once a colour is picked). Fresh array per row so
      // edits/removals don't leak across rows. The supplier can still remove or
      // add more.
      const images = seedImages && seedImages.length > 0 ? [...seedImages] : []
      return [
        ...prev,
        {id: nextId, sizeId: -1, colorId: -1, price: '', qty: '', sku: '', images, status: 'idle'},
      ]
    })
  }

  const patchRow = (id: number, patch: Partial<VRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? {...r, ...patch} : r)))

  const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id))

  // A colour is "new" when the parent supplied the existing colours and the
  // chosen one isn't among them. New colours require their own photo; existing
  // colours / size-only changes don't (they reuse the master's images).
  const isNewColor = (colorId: number) =>
    Array.isArray(existingColorIds) && colorId > 0 && !existingColorIds.includes(colorId)

  const validateRow = (r: VRow): string | null => {
    if (r.sizeId <= 0 && r.colorId <= 0)
      return formatMessage({id: 'offers.variants.err.variation_required'})
    const p = toNum(r.price)
    const q = parseInt(r.qty, 10)
    if (!Number.isFinite(p) || p <= 0) return formatMessage({id: 'offers.form.err.price_invalid'})
    if (!Number.isFinite(q) || q < 0) return formatMessage({id: 'offers.form.err.qty_invalid'})
    if (isNewColor(r.colorId) && r.images.length === 0)
      return formatMessage({id: 'offers.variants.err.color_image_required'})
    return null
  }

  const variantErrMsg = (code: string): string => {
    const id = VARIANT_ERR_CODES.includes(code)
      ? `offers.variants.err.${code}`
      : 'offers.variants.err.generic'
    return formatMessage({id})
  }

  const submitRow = async (r: VRow) => {
    const v = validateRow(r)
    if (v) {
      patchRow(r.id, {status: 'error', error: v})
      return false
    }
    patchRow(r.id, {status: 'submitting', error: undefined})
    try {
      await requestVariantFromBarcode(
        {
          barcode,
          size_id: r.sizeId > 0 ? r.sizeId : undefined,
          color_id: r.colorId > 0 ? r.colorId : undefined,
          price: toNum(r.price),
          quantity: parseInt(r.qty, 10),
          // Photos only for a new-colour variant (a new colour needs its own
          // images; a size-only variant reuses the master's).
          images: r.colorId > 0 && r.images.length > 0 ? r.images : undefined,
        },
        currentUser?.token,
        language,
      )
      patchRow(r.id, {status: 'done'})
      return true
    } catch (err: any) {
      patchRow(r.id, {
        status: 'error',
        error: variantErrMsg(err?.response?.data?.error ?? ''),
      })
      return false
    }
  }

  const submitAll = async () => {
    setSubmitting(true)
    const targets = rows.filter((r) => r.status !== 'done')
    let anyOk = false
    for (const r of targets) {
      const ok = await submitRow(r)
      if (ok) anyOk = true
    }
    setSubmitting(false)
    if (anyOk && rows.every((r) => r.status === 'done' || r.status === 'error')) {
      setAllDone(rows.some((r) => r.status === 'done'))
    }
  }

  const pendingCount = rows.filter((r) => r.status !== 'done').length

  return (
    <div className='avp-root'>
      {/* Trigger button — always visible */}
      <button type='button' className='avp-trigger-btn' onClick={addRow} disabled={submitting}>
        <span className='avp-trigger-plus'>+</span>
        {formatMessage({id: 'add_new_variant_size_color'}) || 'Add new variant Size / Color'}
      </button>

      {/* Row cards */}
      {rows.map((r) => (
        <div key={r.id} className='avp-panel'>
          {r.status === 'done' ? (
            <div className='avp-done-badge'>
              <i className='fas fa-check-circle' aria-hidden='true' />
              {formatMessage({id: 'offers.variants.request_submitted_badge'})}
            </div>
          ) : (
            <>
              {/* SIZE + COLOR */}
              <div className='avp-row-2'>
                <div className='avp-field'>
                  <label className='avp-label'>
                    {(formatMessage({id: 'offers.variants.size'}) || 'Size').toUpperCase()}
                  </label>
                  <select
                    className='avp-select'
                    value={r.sizeId}
                    onChange={(e) => patchRow(r.id, {sizeId: parseInt(e.target.value, 10)})}
                    disabled={submitting}
                  >
                    <option value={-1}>{formatMessage({id: 'size_select'}) || 'Select Size'}</option>
                    {sizeOpts.map((s) => (
                      <option key={s.size_id} value={s.size_id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='avp-field'>
                  <label className='avp-label'>
                    {(formatMessage({id: 'offers.variants.color'}) || 'Color').toUpperCase()}
                  </label>
                  <select
                    className='avp-select'
                    value={r.colorId}
                    onChange={(e) => patchRow(r.id, {colorId: parseInt(e.target.value, 10)})}
                    disabled={submitting}
                  >
                    <option value={-1}>{formatMessage({id: 'color_select'}) || 'Select Color'}</option>
                    {colorOpts.map((c) => (
                      <option key={c.color_id} value={c.color_id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SALE PRICE + SUPPLIER QUANTITY */}
              <div className='avp-row-2'>
                <div className='avp-field'>
                  <label className='avp-label'>
                    {(formatMessage({id: 'offers.form.price'}) || 'Price').toUpperCase()}{' '}
                    <span className='avp-req'>*</span>
                  </label>
                  <input
                    type='number'
                    step='0.01'
                    min='0.01'
                    className='avp-input'
                    placeholder='0.00'
                    value={r.price}
                    onChange={(e) => patchRow(r.id, {price: e.target.value})}
                    disabled={submitting}
                  />
                </div>
                <div className='avp-field'>
                  <label className='avp-label'>
                    {(formatMessage({id: 'supplier_quantity'}) || 'Supplier Quantity').toUpperCase()}{' '}
                    <span className='avp-req'>*</span>
                  </label>
                  <input
                    type='number'
                    min='0'
                    className='avp-input'
                    placeholder='0'
                    value={r.qty}
                    onChange={(e) => patchRow(r.id, {qty: e.target.value})}
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* SUPPLIER SKU */}
              <div className='avp-field'>
                <label className='avp-label'>
                  {(formatMessage({id: 'offers.form.supplier_sku'}) || 'Supplier SKU').toUpperCase()}
                </label>
                <input
                  type='text'
                  maxLength={64}
                  className='avp-input'
                  placeholder={formatMessage({id: 'supplier_sku_placeholder'}) || 'Enter your internal SKU'}
                  value={r.sku}
                  onChange={(e) => patchRow(r.id, {sku: e.target.value})}
                  disabled={submitting}
                />
              </div>

              {/* IMAGES — always visible. Required for a NEW colour (it needs
                  its own photos); optional for an existing colour or a size-only
                  change (those reuse the master's images). */}
              <div className='avp-field'>
                <label className='avp-label'>
                  {formatMessage({id: 'offers.variants.images', defaultMessage: 'Color images'}).toUpperCase()}
                  {isNewColor(r.colorId) ? (
                    <span className='avp-req'> *</span>
                  ) : (
                    <span style={{fontWeight: 400, opacity: 0.6}}> ({formatMessage({id: 'optional'})})</span>
                  )}
                </label>
                <ImagePicker
                  files={r.images}
                  disabled={submitting}
                  onChange={(files) => patchRow(r.id, {images: files})}
                />
              </div>

              {/* Error */}
              {r.status === 'error' && r.error && (
                <div className='avp-error'>{r.error}</div>
              )}

              {/* Actions */}
              <div className='avp-actions'>
                <button
                  type='button'
                  className='avp-cancel-btn'
                  onClick={() => removeRow(r.id)}
                  disabled={submitting}
                >
                  {formatMessage({id: 'cancel'}) || 'Cancel'}
                </button>
                <button
                  type='button'
                  className='avp-submit-btn'
                  onClick={submitAll}
                  disabled={submitting || pendingCount === 0}
                >
                  {submitting
                    ? formatMessage({id: 'offers.variants.submitting_request'}) || 'Submitting…'
                    : formatMessage({id: 'offers.variants.submit_request'}) || 'Submit Request'}
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* All done banner */}
      {allDone && rows.length > 0 && rows.every((r) => r.status === 'done') && (
        <div className='avp-all-done'>
          <i className='fas fa-check-circle' aria-hidden='true' />
          {formatMessage({id: 'offers.variants.request_done'})}
        </div>
      )}
    </div>
  )
}

export default AddVariantsPanel
